import { getCachedViewSnapshotForSelections } from '@databases/database-state';
import { type MessageFormatter } from '@databases/intl/intl-types';
import { type PanelManager } from '@databases/panel-types';
import {
  buildCellActions,
  buildColumnActions,
  buildRowActions,
} from '@databases/selection-menu-actions';
import { createEffect } from '@databases/selections';
import { type SelectionsApi } from '@databases/selections-api';
import { type DatabaseAdapter } from '@databases/selections-api-types';
import { type SelectionsActions } from '@databases/selections-api-types';
import { type ViewChangeType } from '@databases/utils/database/view/view';

import { contextMenuState } from './state/context-menu-state';
import { createSelectValueMenuContext } from './utils/create-select-value-menu-context';

export const createContextMenuEffect = createEffect(
  ({
    selectionsApiFactory,
    databaseAdapterFactory,
    contentId,
    formatMessage,
    panelManager,
    selectionsActionsFactory,
    currentViewIdFactory,
  }: {
    selectionsApiFactory: () => SelectionsApi;
    databaseAdapterFactory: () => DatabaseAdapter;
    contentId: string;
    formatMessage: MessageFormatter;
    panelManager: PanelManager;
    selectionsActionsFactory: () => SelectionsActions;
    currentViewIdFactory: () => string;
  }) => {
    return {
      dispose() {
        contextMenuState.hide();
      },

      deltaTypes: ['selectionReplaced', 'cellEditRequested'],

      onDelta(delta) {
        if (delta.kind === 'cellEditRequested' || !delta.contextMenuTrigger) {
          contextMenuState.hide();

          return;
        }

        const focusedPosition = delta.focusChange?.next;

        if (!focusedPosition) {
          contextMenuState.hide();

          return;
        }

        const selectionsApi = selectionsApiFactory();
        const databaseAdapter = databaseAdapterFactory();

        const { entryId, fieldId } = selectionsApi.resolvePositionToIds(
          focusedPosition,
          'cell'
        );

        if (!entryId || !fieldId) {
          contextMenuState.hide();

          return;
        }

        const context = createSelectValueMenuContext({
          canEditContent: databaseAdapter.canEditContent(),
          canEditFields: databaseAdapter.canEditFields(),
          closeMenu: () => contextMenuState.hide(),
          contentId,
          currentViewId: currentViewIdFactory(),
          databaseAdapter,
          entryId,
          fieldId,
          formatMessage,
          panelManager,
          selectionsActions: selectionsActionsFactory(),
          selectionsApi,
        });

        if (!context) {
          contextMenuState.hide();

          return;
        }

        const view = getCachedViewSnapshotForSelections();
        const actionsBuilder = (setViewChange: (view: ViewChangeType) => void) => ({
          row: buildRowActions({
            contentId,
            selectionsApi,
            view,
            databaseAdapter,
            spaceKey: databaseAdapter.getSpaceKey(),
            panelManager,
          }),
          column: buildColumnActions({
            contentId,
            selectionsApi,
            view,
            databaseAdapter,
            setViewChange,
            formatMessage,
          }),
          cell: buildCellActions({
            contentId,
            selectionsApi,
            panelManager,
          }),
        });

        contextMenuState.show(delta.contextMenuTrigger, context, actionsBuilder);
      },
    };
  }
);
